import {
    getAnalyticsDataKey,
} from '@/utils/analytics';
import {
    filter,
    kebabCase,
    omit,
    pickBy,
    upperFirst,
} from 'lodash';
import apiCall from '@/utils/apiCall';

/**
 * Helper function for analytics actions.
 *
 * @param {object}   options                Vuex store.
 * @param {Function} options.commit         Method to commit a mutation.
 * @param {Function} options.filterCallback Data filter callback.
 * @param {string}   options.force          Whether to force reload the data.
 * @param {string}   options.from           Start date timestamp.
 * @param {object}   options.instance       Vue instance.
 * @param {object}   options.params         Additional request params.
 * @param {string}   options.showHash       Show hash.
 * @param {object}   options.state          Vuex state.
 * @param {string}   options.stateKey       State key.
 * @param {string}   options.to             End date timestamp.
 * @param {string}   options.urlPath        Url path in `analitics` endpoint
 * @returns {Promise}                       Promise.
 */
const helper = async ({
    commit,
    filterCallback,
    force,
    from,
    instance,
    params = {},
    showHash,
    state,
    stateKey,
    to,
    urlPath,
}) => {
    const key = getAnalyticsDataKey({
        from,
        showHash,
        to,
        ...omit(params, ['limit']),
    });

    if (
        ! force &&
        state[stateKey]?.[key] !== undefined &&
        (! params.limit || state[stateKey]?.[key]?.length >= params.limit)
    ) {
        return;
    }

    const mutationName = `set${upperFirst(stateKey)}`;

    commit(mutationName, {
        key,
        value: null,
    });

    const preparedParams = pickBy({
        ...params,
        from,
        to,
    }, value => !! value);

    const path = urlPath || kebabCase(stateKey);
    const url = showHash
        ? `/api/shows/${showHash}/analytics/${path}`
        : `/api/shows/analytics/${path}`;

    const {
        status,
        data,
    } = await apiCall(instance, 'get', url, {
        params: preparedParams,
    });

    const value = 200 === status
        ? (filterCallback ? filter(data, filterCallback) : data)
        : false;

    commit(mutationName, {
        key,
        value,
    });
};

export default {
    /**
     * Loads top listens analytics data.
     *
     * @param {object}   store            Vuex store.
     * @param {object}   store.state      Vuex state.
     * @param {Function} store.commit     Method to commit a mutation.
     * @param {object}   payload          Payload data.
     * @param {string}   payload.force    Whether to force reload the data.
     * @param {string}   payload.from     Start date timestamp.
     * @param {string}   payload.limit    Number of items to fetch.
     * @param {string}   payload.showHash Show hash.
     * @param {string}   payload.to       End date timestamp.
     * @returns {Promise}                 Promise.
     */
    async loadTopListens({
        state,
        commit,
    }, {
        force = false,
        from = null,
        limit = null,
        showHash = null,
        to = null,
    } = {}) {
        return helper({
            commit,
            filterCallback: item => !! item.episode && !! item.show,
            force,
            from,
            instance: this._vm,
            params: { limit: limit || (showHash ? 10 : 5) },
            showHash,
            state,
            stateKey: 'topListens',
            to,
            urlPath: 'top-episodes',
        });
    },

    /**
     * Loads listens data for bar chart.
     *
     * @param {object}   store            Vuex store.
     * @param {object}   store.state      Vuex state.
     * @param {Function} store.commit     Method to commit a mutation.
     * @param {object}   payload          Payload data.
     * @param {string}   payload.bucket   Data grouping bucket.
     * @param {string}   payload.force    Whether to force reload the data.
     * @param {string}   payload.from     Start date timestamp.
     * @param {string}   payload.limit    Number of items to fetch.
     * @param {string}   payload.showHash Show hash.
     * @param {string}   payload.to       End date timestamp.
     * @returns {Promise}                 Promise.
     */
    async loadTopShows({
        state,
        commit,
    }, {
        bucket = null,
        force = false,
        from = null,
        limit = null,
        showHash = null,
        to = null,
    }) {
        return helper({
            commit,
            force,
            from,
            instance: this._vm,
            state,
            stateKey: 'topShows',
            to,
        });
    },

    /**
     * Loads listens data for bar chart.
     *
     * @param {object}   store            Vuex store.
     * @param {object}   store.state      Vuex state.
     * @param {Function} store.commit     Method to commit a mutation.
     * @param {object}   payload          Payload data.
     * @param {string}   payload.bucket   Data grouping bucket.
     * @param {string}   payload.force    Whether to force reload the data.
     * @param {string}   payload.from     Start date timestamp.
     * @param {string}   payload.limit    Number of items to fetch.
     * @param {string}   payload.showHash Show hash.
     * @param {string}   payload.to       End date timestamp.
     * @returns {Promise}                 Promise.
     */
    async loadListens({
        state,
        commit,
    }, {
        bucket = null,
        force = false,
        from = null,
        limit = null,
        showHash = null,
        to = null,
    }) {
        return helper({
            commit,
            force,
            from,
            instance: this._vm,
            params: { bucket },
            showHash,
            state,
            stateKey: 'listens',
            to,
        });
    },

    /**
     * Loads sources data for doughnut chart.
     *
     * @param {object}   store            Vuex store.
     * @param {object}   store.state      Vuex state.
     * @param {Function} store.commit     Method to commit a mutation.
     * @param {object}   payload          Payload data.
     * @param {string}   payload.bucket   Data grouping bucket.
     * @param {string}   payload.force    Whether to force reload the data.
     * @param {string}   payload.from     Start date timestamp.
     * @param {string}   payload.limit    Number of items to fetch.
     * @param {string}   payload.showHash Show hash.
     * @param {string}   payload.to       End date timestamp.
     * @returns {Promise}                 Promise.
     */
    async loadSources({
        state,
        commit,
    }, {
        force = false,
        from = null,
        showHash = null,
        to = null,
    }) {
        return helper({
            commit,
            force,
            from,
            instance: this._vm,
            showHash,
            state,
            stateKey: 'sources',
            to,
            urlPath: 'listens/sources',
        });
    },

    /**
     * Loads platforms data for doughnut chart.
     *
     * @param {object}   store            Vuex store.
     * @param {object}   store.state      Vuex state.
     * @param {Function} store.commit     Method to commit a mutation.
     * @param {object}   payload          Payload data.
     * @param {string}   payload.bucket   Data grouping bucket.
     * @param {string}   payload.force    Whether to force reload the data.
     * @param {string}   payload.from     Start date timestamp.
     * @param {string}   payload.limit    Number of items to fetch.
     * @param {string}   payload.showHash Show hash.
     * @param {string}   payload.to       End date timestamp.
     * @returns {Promise}                 Promise.
     */
    async loadPlatforms({
        state,
        commit,
    }, {
        force = false,
        from = null,
        showHash = null,
        to = null,
    }) {
        return helper({
            commit,
            force,
            from,
            instance: this._vm,
            showHash,
            state,
            stateKey: 'platforms',
            to,
            urlPath: 'listens/platforms',
        });
    },
};
